import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Loader2, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import {
    generateLauraP0Briefing,
    generateLauraP0Questions,
    getLauraP0State,
    linkLauraAuditDocument,
    patchLauraP0Answers,
    patchLauraP0Context,
    patchLauraP0Validation,
    unlinkLauraAuditDocument,
    updateLauraDeliverable,
} from "@/app/lib/auditflowApi";
import type {
    LauraAudit,
    LauraAuditDocument,
    LauraP0Context,
    LauraP0Question,
    LauraP0State,
    LauraP0ValidationItem,
} from "@/app/components/shared/types";
import { useTranslation } from "@/app/i18n/LocaleContext";
import { LAURA_STAGES } from "@/app/components/laura/stages";
import { LauraProjectDocumentPicker } from "./LauraProjectDocumentPicker";
import { p0CategoryLabel } from "./p0CategoryLabel";
import { GrowTextarea } from "@/app/components/shared/GrowTextarea";
import { P0StepBadge } from "./P0StepBadge";
import { P0WorkflowHelp } from "./P0WorkflowHelp";
import {
    getP0StepStatus,
    type P0StepStatus,
    type P0WorkflowState,
} from "./p0Workflow";
import { PlanningProgressHeader } from "../shared/PlanningProgressHeader";
import { ValidationCheckpointPanel } from "../shared/ValidationCheckpointPanel";
import { Badge } from "@/components/ui/badge";

interface Props {
    audit: LauraAudit;
    deliverableContent: string;
    onDeliverableSaved: (content: string) => void;
    onP0StateChange?: (state: LauraP0State | null) => void;
}

function isApiConflict(err: unknown): boolean {
    return err instanceof Error && err.message.includes("confirm_invalidate");
}

function p0SectionClass(status: P0StepStatus): string {
    return cn(
        "rounded-lg border transition-colors",
        status === "current" &&
            "border-blue-400 bg-blue-50/40 ring-1 ring-blue-200",
        status === "done" && "border-emerald-200/90 bg-white",
        status === "pending" && "border-gray-200 bg-white",
    );
}

function countPlanningInputs(documents: LauraAuditDocument[]): number {
    return documents.filter((d) => d.role === "planning_input").length;
}

function patchPlanningDocumentLink(
    state: LauraP0State,
    auditId: string,
    doc: { id: string; filename: string },
    linked: boolean,
    serverRow?: LauraAuditDocument,
): LauraP0State {
    const rest = (state.documents ?? []).filter(
        (d) => !(d.document_id === doc.id && d.role === "planning_input"),
    );
    if (!linked) {
        return {
            ...state,
            documents: rest,
            planning_input_count: countPlanningInputs(rest),
        };
    }
    const row: LauraAuditDocument =
        serverRow ?? {
            id: `pending-${doc.id}`,
            audit_id: auditId,
            document_id: doc.id,
            role: "planning_input",
        };
    const documents = [...rest, row];
    return {
        ...state,
        documents,
        planning_input_count: countPlanningInputs(documents),
    };
}

export function LauraP0Workspace({
    audit,
    deliverableContent,
    onDeliverableSaved,
    onP0StateChange,
}: Props) {
    const { t } = useTranslation();
    const [p0State, setP0State] = useState<LauraP0State | null>(null);
    const [loading, setLoading] = useState(true);
    const [comments, setComments] = useState("");
    const [briefingDraft, setBriefingDraft] = useState(deliverableContent);
    const [generatingQuestions, setGeneratingQuestions] = useState(false);
    const [generatingBriefing, setGeneratingBriefing] = useState(false);
    const [savingBriefing, setSavingBriefing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [invalidateModal, setInvalidateModal] = useState<{
        action: () => Promise<void>;
    } | null>(null);
    const answersDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
    const answersSaveSeq = useRef(0);
    /** Latest typed answers; avoids stale closure + stale PATCH overwrites. */
    const answersDraftRef = useRef<Record<string, string>>({});

    const validationDraftRef = useRef<Record<string, boolean>>({});

    function syncAnswersDraft(questions: LauraP0Question[] | undefined) {
        const draft: Record<string, string> = {};
        for (const q of questions ?? []) {
            draft[q.id] = q.answer ?? "";
        }
        answersDraftRef.current = draft;
    }

    function syncValidationDraft(items: LauraP0ValidationItem[] | undefined) {
        const draft: Record<string, boolean> = {};
        for (const i of items ?? []) {
            draft[i.id] = i.checked;
        }
        validationDraftRef.current = draft;
    }

    function mergeValidationItemsFromDraft(
        items: LauraP0ValidationItem[],
    ): LauraP0ValidationItem[] {
        return items.map((item) => {
            const draft = validationDraftRef.current[item.id];
            const checked =
                draft !== undefined ? draft : item.checked;
            return {
                ...item,
                checked,
                checked_at: checked
                    ? item.checked_at ?? new Date().toISOString()
                    : undefined,
            };
        });
    }

    const validationDebounceRef = useRef<
        Record<string, ReturnType<typeof setTimeout>>
    >({});
    const p0StateRef = useRef(p0State);
    p0StateRef.current = p0State;

    const [validationItems, setValidationItems] = useState<
        LauraP0ValidationItem[]
    >([]);
    const validationItemsRef = useRef(validationItems);
    validationItemsRef.current = validationItems;

    const onDeliverableSavedRef = useRef(onDeliverableSaved);
    onDeliverableSavedRef.current = onDeliverableSaved;

    /** Preserve in-flight answer + validation drafts when merging any server p0 payload. */
    function mergeP0WithLocalDrafts(
        serverP0: LauraP0Context,
        localP0: LauraP0Context,
    ): LauraP0Context {
        const questions = (serverP0.questions ?? localP0.questions ?? []).map(
            (q) => ({
                ...q,
                answer:
                    answersDraftRef.current[q.id] ?? q.answer ?? "",
            }),
        );
        const validationBase =
            serverP0.validation_items ?? localP0.validation_items ?? [];
        return {
            ...serverP0,
            questions,
            validation_items: mergeValidationItemsFromDraft(validationBase),
        };
    }

    const contextRef = useRef<HTMLElement>(null);
    const [contextExpanded, setContextExpanded] = useState(true);

    const projectId = audit.project_id;
    const p0 = p0State?.p0;
    const isP0Approved = Boolean(p0State?.completion);
    const readOnly = isP0Approved || audit.current_stage !== "p0";

    const planningCompletions = useMemo(() => {
        const done = new Set(
            (audit.stage_completions ?? [])
                .filter((c) => c.stage.startsWith("p"))
                .map((c) => c.stage),
        );
        return done.size;
    }, [audit.stage_completions]);

    const linkedPlanningIds = useMemo(() => {
        const ids = new Set<string>();
        for (const row of p0State?.documents ?? []) {
            if (row.role === "planning_input") ids.add(row.document_id);
        }
        return ids;
    }, [p0State?.documents]);

    const validationSig = useMemo(
        () =>
            validationItems
                .map((i) => `${i.id}:${i.checked ? 1 : 0}`)
                .join("|"),
        [validationItems],
    );

    useEffect(() => {
        const s = p0StateRef.current;
        if (!s || validationItems.length === 0) return;
        onP0StateChange?.({
            ...s,
            p0: { ...s.p0, validation_items: validationItems },
        });
    }, [validationSig, onP0StateChange, validationItems.length]);

    const reload = useCallback(async () => {
        const state = await getLauraP0State(audit.id);
        syncAnswersDraft(state.p0.questions);
        syncValidationDraft(state.p0.validation_items);
        const mergedValidation = mergeValidationItemsFromDraft(
            state.p0.validation_items ?? [],
        );
        setValidationItems(mergedValidation);
        const stateWithValidation = {
            ...state,
            p0: { ...state.p0, validation_items: mergedValidation },
        };
        setP0State(stateWithValidation);
        onP0StateChange?.(stateWithValidation);
        setComments(state.p0.initial_comments ?? "");
        if (state.deliverable?.content_md) {
            setBriefingDraft(state.deliverable.content_md);
            onDeliverableSavedRef.current(state.deliverable.content_md);
        }
        return stateWithValidation;
    }, [audit.id, onP0StateChange]);

    useEffect(() => {
        reload().finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps -- load once per audit
    }, [audit.id]);

    useEffect(() => {
        setBriefingDraft(deliverableContent);
    }, [deliverableContent]);

    async function saveComments(confirmInvalidate = false) {
        try {
            const { p0: updated } = await patchLauraP0Context(audit.id, {
                initial_comments: comments,
                confirm_invalidate: confirmInvalidate,
            });
            setP0State((s) => (s ? { ...s, p0: updated } : s));
            setError(null);
        } catch (err) {
            if (isApiConflict(err) && !confirmInvalidate) {
                setInvalidateModal({
                    action: async () => {
                        await saveComments(true);
                        await reload();
                    },
                });
                return;
            }
            setError(err instanceof Error ? err.message : String(err));
        }
    }

    function applyPlanningDocLink(
        doc: { id: string; filename: string },
        linked: boolean,
        serverRow?: LauraAuditDocument,
    ) {
        setP0State((s) => {
            if (!s) return s;
            return patchPlanningDocumentLink(
                s,
                audit.id,
                doc,
                linked,
                serverRow,
            );
        });
    }

    async function handleDocToggle(
        doc: { id: string; filename: string },
        linked: boolean,
        confirmInvalidate = false,
    ) {
        if (readOnly) return;
        applyPlanningDocLink(doc, linked);
        try {
            if (linked) {
                const row = await linkLauraAuditDocument(audit.id, {
                    document_id: doc.id,
                    role: "planning_input",
                    confirm_invalidate: confirmInvalidate,
                });
                applyPlanningDocLink(doc, true, row);
            } else {
                await unlinkLauraAuditDocument(
                    audit.id,
                    doc.id,
                    confirmInvalidate,
                );
            }
            if (confirmInvalidate) {
                await reload();
            }
            setError(null);
        } catch (err) {
            if (isApiConflict(err) && !confirmInvalidate) {
                applyPlanningDocLink(doc, !linked);
                setInvalidateModal({
                    action: async () => {
                        await handleDocToggle(doc, linked, true);
                    },
                });
                return;
            }
            await reload();
            setError(err instanceof Error ? err.message : String(err));
        }
    }

    async function handleGenerateQuestions() {
        setGeneratingQuestions(true);
        setError(null);
        try {
            await saveComments(false);
            await generateLauraP0Questions(audit.id);
            await reload();
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setGeneratingQuestions(false);
        }
    }

    function scheduleSaveAnswers() {
        if (answersDebounce.current) clearTimeout(answersDebounce.current);
        const scheduledSeq = ++answersSaveSeq.current;
        answersDebounce.current = setTimeout(async () => {
            const map = { ...answersDraftRef.current };
            try {
                const { p0: updated } = await patchLauraP0Answers(audit.id, map);
                setP0State((s) => {
                    if (!s?.p0.questions) {
                        return s ? { ...s, p0: updated } : s;
                    }
                    if (scheduledSeq !== answersSaveSeq.current) return s;
                    const merged = mergeP0WithLocalDrafts(updated, s.p0);
                    return {
                        ...s,
                        p0: {
                            ...merged,
                            validation_items: mergeValidationItemsFromDraft(
                                validationItemsRef.current.length > 0
                                    ? validationItemsRef.current
                                    : (s.p0.validation_items ?? []),
                            ),
                        },
                    };
                });
            } catch (err) {
                setError(err instanceof Error ? err.message : String(err));
            }
        }, 500);
    }

    function updateAnswer(questionId: string, answer: string) {
        answersDraftRef.current[questionId] = answer;
        setP0State((s) => {
            if (!s?.p0.questions) return s;
            const questions = s.p0.questions.map((q) =>
                q.id === questionId ? { ...q, answer } : q,
            );
            return { ...s, p0: { ...s.p0, questions } };
        });
        scheduleSaveAnswers();
    }

    const allRequiredFilled = useMemo(() => {
        const qs = p0?.questions ?? [];
        if (qs.length === 0) return false;
        return qs
            .filter((q) => q.required)
            .every((q) => (q.answer ?? "").trim().length > 0);
    }, [p0?.questions]);

    async function handleGenerateBriefing() {
        setGeneratingBriefing(true);
        setError(null);
        try {
            const result = await generateLauraP0Briefing(audit.id);
            setBriefingDraft(result.briefing_md);
            onDeliverableSaved(result.briefing_md);
            await reload();
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setGeneratingBriefing(false);
        }
    }

    async function saveBriefing() {
        setSavingBriefing(true);
        try {
            await updateLauraDeliverable(audit.id, "p0", briefingDraft);
            onDeliverableSaved(briefingDraft);
        } finally {
            setSavingBriefing(false);
        }
    }

    function scheduleValidationPersist(itemId: string) {
        const prev = validationDebounceRef.current[itemId];
        if (prev) clearTimeout(prev);

        const snapshotChecked = validationDraftRef.current[itemId] ?? false;

        validationDebounceRef.current[itemId] = setTimeout(() => {
            const checked = validationDraftRef.current[itemId] ?? false;
            void patchLauraP0Validation(audit.id, itemId, checked).catch(
                (err) => {
                    const revertChecked = snapshotChecked;
                    validationDraftRef.current[itemId] = revertChecked;
                    setValidationItems((items) =>
                        items.map((i) =>
                            i.id === itemId
                                ? {
                                      ...i,
                                      checked: revertChecked,
                                      checked_at: revertChecked
                                          ? i.checked_at
                                          : undefined,
                                  }
                                : i,
                        ),
                    );
                    setError(
                        err instanceof Error ? err.message : String(err),
                    );
                },
            );
        }, 400);
    }

    function toggleValidation(item: LauraP0ValidationItem, checked: boolean) {
        validationDraftRef.current[item.id] = checked;
        const nextItems = validationItemsRef.current.map((i) =>
            i.id === item.id
                ? {
                      ...i,
                      checked,
                      checked_at: checked
                          ? i.checked_at ?? new Date().toISOString()
                          : undefined,
                  }
                : i,
        );
        setValidationItems(nextItems);
        setP0State((s) => {
            if (!s) return s;
            const next = {
                ...s,
                p0: { ...s.p0, validation_items: nextItems },
            };
            onP0StateChange?.(next);
            return next;
        });
        scheduleValidationPersist(item.id);
    }

    const hasBriefing = (briefingDraft ?? "").trim().length > 0;
    const deliverableOk = hasBriefing;

    const savedComments = p0?.initial_comments ?? "";

    const workflowState: P0WorkflowState = useMemo(
        () => ({
            linkedDocCount: linkedPlanningIds.size,
            comments,
            savedComments,
            questionCount: p0?.questions?.length ?? 0,
            allRequiredFilled,
            hasBriefing,
            isApproved: isP0Approved,
        }),
        [
            linkedPlanningIds.size,
            comments,
            savedComments,
            p0?.questions?.length,
            allRequiredFilled,
            hasBriefing,
            isP0Approved,
        ],
    );

    const commentsSaved = comments === savedComments;

    useEffect(() => {
        function onPointerDown(e: MouseEvent) {
            if (!contextExpanded || readOnly) return;
            const el = contextRef.current;
            if (!el || el.contains(e.target as Node)) return;
            setContextExpanded(false);
        }
        document.addEventListener("mousedown", onPointerDown);
        return () => document.removeEventListener("mousedown", onPointerDown);
    }, [contextExpanded, readOnly]);

    if (loading || !p0State || !p0) {
        return (
            <div className="flex h-full items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
        );
    }

    if (!projectId) {
        return (
            <p className="p-6 text-sm text-red-600">
                {t("laura", "projectRequired")}
            </p>
        );
    }

    const saiName = audit.laura_sai_profiles?.name;

    return (
        <div className="flex h-full flex-col overflow-hidden">
            <div className="flex flex-col gap-2 border-b px-4 py-3">
                <PlanningProgressHeader currentStage="p0" saiName={saiName} />
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <Badge variant={deliverableOk ? "default" : "secondary"}>
                        {t("laura", "p0Deliverable")}:{" "}
                        {deliverableOk
                            ? t("laura", "p0DeliverableDone")
                            : t("laura", "p0DeliverablePending")}
                    </Badge>
                    <span>
                        {t("laura", "p0Progress")}: {planningCompletions}/8
                    </span>
                </div>
            </div>

            <div className="flex flex-1 flex-col gap-3 overflow-y-auto p-3">
                <h2 className="text-base font-medium text-gray-900">
                    {t("laura", "p0Title")}
                </h2>

                {!readOnly && (
                    <div className="w-full lg:max-w-[calc(50%-0.5rem)]">
                        <P0WorkflowHelp workflow={workflowState} />
                    </div>
                )}

                {error && (
                    <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                        {error}
                    </p>
                )}

                <section
                    ref={contextRef}
                    className={cn(
                        "w-full lg:max-w-[calc(50%-0.5rem)]",
                        p0SectionClass(getP0StepStatus(1, workflowState)),
                    )}
                >
                    <button
                        type="button"
                        onClick={() => setContextExpanded((e) => !e)}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left"
                    >
                        <P0StepBadge
                            step={1}
                            status={getP0StepStatus(1, workflowState)}
                        />
                        <span className="flex-1 text-sm font-medium text-gray-900">
                            {contextExpanded
                                ? t("laura", "p0ContextTitle")
                                : t("laura", "p0ContextSummary", {
                                      count: linkedPlanningIds.size,
                                      commentsStatus: commentsSaved
                                          ? t("laura", "p0CommentsSaved")
                                          : t("laura", "p0CommentsUnsaved"),
                                  })}
                        </span>
                        <ChevronDown
                            className={cn(
                                "h-4 w-4 text-gray-500 transition-transform",
                                contextExpanded && "rotate-180",
                            )}
                        />
                        <span className="sr-only">
                            {contextExpanded
                                ? t("laura", "p0ContextCollapse")
                                : t("laura", "p0ContextExpand")}
                        </span>
                    </button>

                    {contextExpanded && (
                        <div className="space-y-3 border-t px-3 pb-3 pt-2">
                            <div
                                className={cn(
                                    "rounded-md border p-2",
                                    getP0StepStatus(1, workflowState) ===
                                        "current" &&
                                        "border-sky-300 bg-sky-50/50",
                                )}
                            >
                                <div className="mb-1 flex items-center gap-2">
                                    <P0StepBadge
                                        step={1}
                                        status={getP0StepStatus(1, workflowState)}
                                        size="sm"
                                    />
                                    <label className="text-xs font-medium text-gray-700">
                                        {t("laura", "p0SelectDocs")}
                                    </label>
                                </div>
                                <p className="mb-1.5 text-xs text-gray-500">
                                    {t("laura", "p0SelectDocsHint")}
                                </p>
                                <LauraProjectDocumentPicker
                                    projectId={projectId}
                                    linkedIds={linkedPlanningIds}
                                    onToggle={handleDocToggle}
                                    disabled={readOnly}
                                />
                            </div>

                            <div
                                className={cn(
                                    "rounded-md border p-2",
                                    getP0StepStatus(2, workflowState) ===
                                        "current" &&
                                        "border-indigo-300 bg-indigo-50/40",
                                )}
                            >
                                <div className="mb-1 flex items-center gap-2">
                                    <P0StepBadge
                                        step={2}
                                        status={getP0StepStatus(2, workflowState)}
                                        size="sm"
                                        optional
                                    />
                                    <label className="text-xs font-medium text-gray-700">
                                        {t("laura", "p0InitialComments")}
                                    </label>
                                </div>
                                <GrowTextarea
                                    className="w-full rounded-lg border px-3 py-2 text-sm min-h-[64px]"
                                    value={comments}
                                    disabled={readOnly}
                                    placeholder={t(
                                        "laura",
                                        "p0InitialCommentsPlaceholder",
                                    )}
                                    onChange={(e) => setComments(e.target.value)}
                                    onBlur={() => {
                                        if (
                                            comments !== savedComments &&
                                            !readOnly
                                        ) {
                                            void saveComments(false);
                                        }
                                    }}
                                />
                            </div>

                            <div
                                className={cn(
                                    "flex flex-wrap items-center gap-2 rounded-md border p-2",
                                    getP0StepStatus(3, workflowState) ===
                                        "current" &&
                                        "border-violet-300 bg-violet-50/40",
                                )}
                            >
                                <P0StepBadge
                                    step={3}
                                    status={getP0StepStatus(3, workflowState)}
                                    size="sm"
                                />
                                {!readOnly && (
                                    <button
                                        type="button"
                                        className="text-xs font-medium text-violet-800 hover:underline"
                                        onClick={() => saveComments(false)}
                                    >
                                        {t("laura", "p0SaveComments")}
                                    </button>
                                )}
                            </div>

                            <div
                                className={cn(
                                    "flex flex-wrap items-center justify-between gap-2 rounded-md border p-2",
                                    getP0StepStatus(4, workflowState) ===
                                        "current" &&
                                        "border-blue-400 bg-blue-50/50",
                                )}
                            >
                                <div className="flex items-center gap-2">
                                    <P0StepBadge
                                        step={4}
                                        status={getP0StepStatus(4, workflowState)}
                                        size="sm"
                                    />
                                </div>
                                {!readOnly && (
                                    <button
                                        type="button"
                                        disabled={
                                            generatingQuestions ||
                                            linkedPlanningIds.size < 1
                                        }
                                        onClick={handleGenerateQuestions}
                                        className="flex items-center gap-2 rounded-lg bg-blue-700 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                                    >
                                        {generatingQuestions ? (
                                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                        ) : (
                                            <Sparkles className="h-3.5 w-3.5" />
                                        )}
                                        {generatingQuestions
                                            ? t("laura", "p0Generating")
                                            : t("laura", "p0GenerateQuestions")}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </section>

                <div className="grid gap-3 lg:grid-cols-2">
                    <section
                        className={cn(
                            "p-3 space-y-2",
                            p0SectionClass(getP0StepStatus(5, workflowState)),
                        )}
                    >
                        <div className="flex items-center gap-2">
                            <P0StepBadge
                                step={5}
                                status={getP0StepStatus(5, workflowState)}
                            />
                            <h3 className="text-sm font-medium text-gray-900">
                                {t("laura", "p0QuestionsTitle")}
                            </h3>
                            {(p0.questions?.length ?? 0) > 0 && (
                                <span className="text-xs text-gray-500">
                                    ({p0.questions!.length})
                                </span>
                            )}
                        </div>
                        {(p0.questions?.length ?? 0) === 0 ? (
                            <p className="text-sm text-gray-500">
                                {t("laura", "p0QuestionsEmpty")}
                            </p>
                        ) : (
                            <div className="space-y-2">
                                {p0.questions!.map((q, i) => (
                                    <div
                                        key={q.id}
                                        className="rounded border border-gray-100 bg-gray-50/80 p-2 text-sm"
                                    >
                                        <span className="text-[10px] font-medium uppercase tracking-wide text-gray-500">
                                            {p0CategoryLabel(q.category, t)}
                                        </span>
                                        <p className="mt-0.5 text-xs font-medium leading-snug text-gray-800">
                                            {i + 1}. {q.text}
                                        </p>
                                        <GrowTextarea
                                            className="mt-1 w-full rounded border px-2 py-1 text-sm min-h-[2.25rem] bg-white"
                                            disabled={readOnly}
                                            placeholder={t(
                                                "laura",
                                                "p0WaitingAnswer",
                                            )}
                                            value={q.answer ?? ""}
                                            onChange={(e) =>
                                                updateAnswer(
                                                    q.id,
                                                    e.target.value,
                                                )
                                            }
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>

                    <section
                        className={cn(
                            "p-3 space-y-2",
                            p0SectionClass(getP0StepStatus(6, workflowState)),
                        )}
                    >
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                                <P0StepBadge
                                    step={6}
                                    status={getP0StepStatus(6, workflowState)}
                                />
                                <h3 className="text-sm font-medium text-gray-900">
                                    {t("laura", "p0BriefingTitle")}
                                </h3>
                            </div>
                            {!readOnly && (
                                <button
                                    type="button"
                                    disabled={
                                        generatingBriefing ||
                                        !allRequiredFilled ||
                                        (p0.questions?.length ?? 0) === 0
                                    }
                                    onClick={handleGenerateBriefing}
                                    className="flex shrink-0 items-center gap-1 rounded-lg bg-teal-700 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                                >
                                    {generatingBriefing && (
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                    )}
                                    {generatingBriefing
                                        ? t("laura", "p0Generating")
                                        : t("laura", "p0GenerateBriefing")}
                                </button>
                            )}
                        </div>
                        {!hasBriefing ? (
                            <p className="text-sm text-gray-500">
                                {t("laura", "p0BriefingEmpty")}
                            </p>
                        ) : (
                            <GrowTextarea
                                className="w-full rounded border px-2 py-1.5 text-sm font-mono min-h-[120px]"
                                value={briefingDraft}
                                disabled={readOnly}
                                onChange={(e) =>
                                    setBriefingDraft(e.target.value)
                                }
                                onBlur={() => {
                                    if (
                                        briefingDraft !== deliverableContent &&
                                        !readOnly
                                    ) {
                                        void saveBriefing();
                                    }
                                }}
                            />
                        )}
                        {hasBriefing && !readOnly && (
                            <button
                                type="button"
                                disabled={savingBriefing}
                                className="text-xs text-gray-600 hover:underline"
                                onClick={() => saveBriefing()}
                            >
                                {savingBriefing ? "…" : "Save briefing"}
                            </button>
                        )}
                    </section>
                </div>

                <section
                    className={cn(
                        "p-3 space-y-2",
                        p0SectionClass(getP0StepStatus(7, workflowState)),
                    )}
                >
                    <div className="flex items-center gap-2">
                        <P0StepBadge
                            step={7}
                            status={getP0StepStatus(7, workflowState)}
                        />
                        <h3 className="text-sm font-medium text-gray-900">
                            {t("laura", "p0ValidationTitle")}
                        </h3>
                    </div>
                    {validationItems.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                            {t("laura", "p0ValidationEmpty")}
                        </p>
                    ) : (
                        <ValidationCheckpointPanel
                            title={t("laura", "p0ValidationTitle")}
                            items={validationItems}
                            onToggle={(item, checked) => {
                                if (!readOnly && hasBriefing) {
                                    toggleValidation(item, checked);
                                }
                            }}
                        />
                    )}
                </section>
            </div>

            {invalidateModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
                        <h3 className="text-lg font-medium">
                            {t("laura", "p0InvalidateTitle")}
                        </h3>
                        <p className="mt-2 text-sm text-gray-600">
                            {t("laura", "p0InvalidateMessage")}
                        </p>
                        <div className="mt-4 flex justify-end gap-2">
                            <button
                                type="button"
                                className="rounded-lg px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
                                onClick={() => setInvalidateModal(null)}
                            >
                                {t("laura", "p0InvalidateCancel")}
                            </button>
                            <button
                                type="button"
                                className="rounded-lg bg-red-700 px-4 py-2 text-sm text-white"
                                onClick={async () => {
                                    const action = invalidateModal.action;
                                    setInvalidateModal(null);
                                    await action();
                                }}
                            >
                                {t("laura", "p0InvalidateConfirm")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/** Expose P0 approval gates for LauraApproveBar */
export function useP0ApprovalGates(p0State: LauraP0State | null, briefingContent: string) {
    const p0 = p0State?.p0;
    const hasBriefing = briefingContent.trim().length > 0;
    const allValidation =
        (p0?.validation_items?.length ?? 0) > 0 &&
        (p0?.validation_items ?? []).every((i) => i.checked);
    const canApproveP0 =
        hasBriefing &&
        allValidation &&
        (p0?.status === "briefing" || p0?.status === "approved");
    return { canApproveP0, allValidation, hasBriefing };
}

export function countPlanningStages(): number {
    return LAURA_STAGES.filter((s) => s.phase === "planning").length;
}
